/**
 * Pick the one active challenge and the one pending invite Home surfaces (specs/home-page AC-26,
 * AC-27) and shape them for the tiles. Pure selectors over the Arena's challenge list, reusing the
 * Arena's own bucket semantics so Home and Arena never disagree about what is "live" or "my move".
 */

import type { ChallengeWithStats } from '../../../types/challenges';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import { bucketChallengesBySection } from '../../challenges/helpers/challengeSectionBuckets.helper';
import {
  buildParticipants,
  calculateDisplayName,
  normalizeUserIdForComparison,
} from '../../challenges/helpers/challengeCardParticipant.helper';
import { getUserName } from '../../challenges/helpers/challengeCardUi.helper';
import { HOME_COPY } from '../constants/home.constants';
import type { HomeActiveTileModel, HomeInviteTileModel, HomeTileAvatar } from '../types/home.types';

/** The list endpoint embeds these maps; `ChallengeWithStats` does not declare them. */
export type ChallengeWithParticipantMaps = ChallengeWithStats & {
  participantEmailsById?: Record<string, string>;
  participantDisplayNamesById?: Record<string, string>;
  participantAvatarsById?: Record<string, string>;
  participantAvatarIdsById?: Record<string, string>;
  participantAvatarConfigsById?: Record<string, EquippedMap | null>;
};

type PickWithMatch = { userId: string; matchId: string; pointsEarned?: number | null; match?: { date?: string | Date } };

const picksOf = (c: ChallengeWithStats): PickWithMatch[] => (c.picks ?? []) as PickWithMatch[];

const myStatus = (c: ChallengeWithStats, userId: string): string | undefined =>
  c.participantStatuses?.find((p) => normalizeUserIdForComparison(p.userId) === normalizeUserIdForComparison(userId))
    ?.status;

const isCompleted = (c: ChallengeWithStats): boolean => String(c.status).toUpperCase() === 'COMPLETED';

/** ISO of the first kickoff: `dateRange.start`, else the earliest pick-embedded match date. */
export function earliestKickoffIso(challenge: ChallengeWithStats): string | null {
  const start = challenge.dateRange?.start;
  if (start != null) {
    const t = new Date(start).getTime();
    if (Number.isFinite(t)) return new Date(t).toISOString();
  }
  let min = Infinity;
  for (const pick of picksOf(challenge)) {
    const d = pick.match?.date;
    if (d == null) continue;
    const t = new Date(d).getTime();
    if (Number.isFinite(t) && t < min) min = t;
  }
  return min === Infinity ? null : new Date(min).toISOString();
}

const kickoffMs = (c: ChallengeWithStats): number => {
  const iso = earliestKickoffIso(c);
  return iso ? new Date(iso).getTime() : Infinity;
};

const byKickoff = (a: ChallengeWithStats, b: ChallengeWithStats) => kickoffMs(a) - kickoffMs(b);

/**
 * Priority: live → my move (picks missing, not a pending invite) → waiting for opponent → active →
 * waiting for friends. My own untaken open-board posts are excluded. Ties by earliest kickoff.
 */
export function selectActiveChallenge(
  challenges: ChallengeWithStats[],
  currentUserId: string,
): ChallengeWithStats | null {
  const buckets = bucketChallengesBySection(challenges, currentUserId);
  const yourMove = buckets.yourMove.filter((c) => myStatus(c, currentUserId) !== 'PENDING');
  const ordered = [buckets.live, yourMove, buckets.waitingForOpponent, buckets.active, buckets.waitingForFriends];
  for (const bucket of ordered) {
    if (bucket.length === 0) continue;
    return [...bucket].sort(byKickoff)[0];
  }
  return null;
}

/** The oldest challenge where I am still a PENDING participant (an unanswered invite). */
export function selectOldestInvite(
  challenges: ChallengeWithStats[],
  currentUserId: string,
): ChallengeWithStats | null {
  const invites = challenges.filter((c) => !isCompleted(c) && myStatus(c, currentUserId) === 'PENDING');
  if (invites.length === 0) return null;
  return [...invites].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )[0];
}

/** Matches whose kickoff is still ahead; falls back to the match count when picks carry no dates. */
export function countMatchesLeft(challenge: ChallengeWithStats, nowMs: number): number {
  const seen = new Map<string, number>();
  for (const pick of picksOf(challenge)) {
    const d = pick.match?.date;
    if (d == null || seen.has(pick.matchId)) continue;
    seen.set(pick.matchId, new Date(d).getTime());
  }
  if (seen.size === 0) return challenge.matchIds?.length ?? challenge.matchCount ?? 0;
  let left = 0;
  for (const t of seen.values()) if (!Number.isFinite(t) || t > nowMs) left += 1;
  return left;
}

/**
 * ISO of the next kickoff still ahead of `nowMs` (dateRange.start when it is ahead, else the earliest
 * pick-embedded match date still ahead); null once every game has started. The Active row re-shapes
 * itself at that moment (AC-37).
 */
export function nextKickoffIso(challenge: ChallengeWithStats, nowMs: number): string | null {
  let min = Infinity;
  const consider = (value: string | Date | null | undefined) => {
    if (value == null) return;
    const t = new Date(value).getTime();
    if (Number.isFinite(t) && t > nowMs && t < min) min = t;
  };
  consider(challenge.dateRange?.start);
  for (const pick of picksOf(challenge)) consider(pick.match?.date);
  return min === Infinity ? null : new Date(min).toISOString();
}

export function avatarFor(challenge: ChallengeWithParticipantMaps, userId: string, name: string): HomeTileAvatar {
  return {
    name,
    avatarId: challenge.participantAvatarIdsById?.[userId] ?? null,
    avatarConfig: challenge.participantAvatarConfigsById?.[userId] ?? null,
    avatarUrl: challenge.participantAvatarsById?.[userId] ?? null,
  };
}

export function pointsFor(challenge: ChallengeWithStats, userId: string): number {
  return picksOf(challenge)
    .filter((p) => normalizeUserIdForComparison(p.userId) === normalizeUserIdForComparison(userId))
    .reduce((sum, p) => sum + (p.pointsEarned ?? 0), 0);
}

export function buildActiveTileModel(
  challenge: ChallengeWithParticipantMaps,
  currentUserId: string,
  nowMs: number,
): HomeActiveTileModel {
  const participants = buildParticipants(challenge, currentUserId);
  const isGroup = challenge.groupId != null;
  // A group tile is titled by the challenge itself (AC-26); the Arena's display-name rule would
  // surface whichever member happens to be listed first.
  const displayName = isGroup
    ? challenge.name || HOME_COPY.GROUP_FALLBACK_NAME
    : calculateDisplayName(challenge, currentUserId, participants);
  const other = participants.find((p) => !p.isCurrentUser) ?? null;
  const kickoff = earliestKickoffIso(challenge);
  const started = kickoff != null && new Date(kickoff).getTime() <= nowMs;

  const mine = challenge.myPoints ?? pointsFor(challenge, currentUserId);
  const score = started && other && picksOf(challenge).length > 0
    ? { mine, theirs: pointsFor(challenge, other.id) }
    : null;

  return {
    challengeId: challenge.id,
    title: isGroup ? displayName : `${HOME_COPY.VS_PREFIX} ${displayName}`,
    isGroup,
    matchesLeft: countMatchesLeft(challenge, nowMs),
    self: avatarFor(challenge, currentUserId, 'You'),
    opponent: other ? avatarFor(challenge, other.id, other.name) : null,
    score,
    kickoff,
  };
}

export function buildInviteTileModel(
  challenge: ChallengeWithParticipantMaps,
): HomeInviteTileModel {
  const inviterId = challenge.creatorId;
  const name = getUserName(inviterId, challenge.participantDisplayNamesById, challenge.participantEmailsById);
  return {
    challengeId: challenge.id,
    inviter: avatarFor(challenge, inviterId, name),
    stakeCoins: challenge.stakeCoins ?? 0,
  };
}
